import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { ModalController, NavController, NavParams } from "@ionic/angular";
import { I_HistoryItem } from "@models/I_HistoryItem";
import { I_HistoryItemRow } from "@models/I_HistoryItemRow";
import { HistoryItem, LicenseTypes, parseTypeEnum } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { TRANSACTION_STATES } from "@models/payments/States";
import { TRANSACTION_HISTORY_TYPES } from "@models/payments/Types";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ExtractDatePipe } from "@pipes/extractDate.pipe";
import { ParseMoney } from "@pipes/parseMoney";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { CashierHistoryDetailRowsPage } from "../cashier-history-detail-rows/cashier-history-detail-rows";

@Component({
  selector: "page-cashier-history-detail",
  templateUrl: "./cashier-history-detail.html",
  styleUrls: ["./cashier-history-detail.scss"],
  standalone: true,
  imports: [CashierHistoryDetailRowsPage, TranslateModule, ParseMoney, ExtractDatePipe, CommonModule],
})
export class CashierHistoryDetailPage implements OnInit {
  history: I_HistoryItem;
  globalVars!: GlobalVarsModel;
  public historyItemRows: I_HistoryItemRow[] = [];
  HistoryItem = HistoryItem;

  public navCtrl = inject(NavController);
  public navParams = inject(NavParams);
  public modalCtrl = inject(ModalController);
  public newBaseService = inject(NewBaseService);
  public deviceService = inject(DeviceService);
  public translate = inject(TranslateService);
  public isDesktop = false;
  public isMobile = false;

  constructor() {
    this.newBaseService.getVars.subscribe((data: GlobalVarsModel) => {
      this.globalVars = data;
    });

    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnInit(): void {
    this.history = this.navParams.get("historyItem");
    this.populateHistoryItemRows();
  }

  close() {
    this.modalCtrl.dismiss();
  }

  cancelPayment() {
    this.modalCtrl.dismiss(this.history);
  }

  get setCancelWithdrawal() {
    return (
      this.history.Estado === TRANSACTION_STATES.pending &&
      this.history.Tipo === TRANSACTION_HISTORY_TYPES.withdrawal
    );
  }

  get displayRetainedAmount() {
    const { AmountIsGreaterThanTaxWithdrawThreshold } = this.history;
    const hasRetainedAmount = "RetainedAmount" in this.history;
    const allowedLicenses = this.globalVars.licenseType === LicenseTypes.Colombia;

    return AmountIsGreaterThanTaxWithdrawThreshold && hasRetainedAmount && allowedLicenses;
  }

  populateHistoryItemRows() {
    const taxRate = this.history?.[HistoryItem.taxRate];
    const taxRateString = taxRate ? ` (${parseFloat(taxRate.toString()).toFixed(2)}%)` : "";

    this.historyItemRows = [
      { title: this.translate.instant("tTrans"), field: HistoryItem.type },
      { title: this.translate.instant("tCodTrans"), field: HistoryItem.transactionCode },
      {
        title: this.translate.instant("tMet"),
        field: HistoryItem.method,
      },
      { title: this.translate.instant("tImp"), field: HistoryItem.amount, parseType: parseTypeEnum.currency },
      {
        title: this.translate.instant("tImpTax"),
        field: HistoryItem.taxAmount,
        parseType: parseTypeEnum.currency,
        suffix: taxRateString,
      },
      {
        title: this.translate.instant("tImpNet"),
        field: HistoryItem.netAmount,
        parseType: parseTypeEnum.currency,
      },
      {
        title: this.translate.instant("tImpRet"),
        field: HistoryItem.retainedAmount,
        parseType: parseTypeEnum.currency,
        hidden: !this.displayRetainedAmount,
      },
      { title: this.translate.instant("hDate"), field: HistoryItem.date, parseType: parseTypeEnum.date },
      { title: this.translate.instant("tNCuenta"), field: HistoryItem.account },
      { title: this.translate.instant("hStade"), field: HistoryItem.status },
    ];
  }
}
